import React, { useRef, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { API_BASE_URL } from "../config";

interface TargetXHeaderProps {
  title: string;
  subtitle: string;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  searchValue?: string;
  showSearch?: boolean;
}

export const TargetXHeader: React.FC<TargetXHeaderProps> = ({
  title,
  subtitle,
  onSearch: _onSearch,
  searchValue: _searchValue = "",
  showSearch = true,
  // searchPlaceholder = "Search for genes, proteins, and drug targets...",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [userInfoSent, setUserInfoSent] = React.useState(false);
  const { accounts, instance } = useMsal();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const account = accounts[0];


  // Send user info to backend when account is available
  useEffect(() => {
    const sendUserInfo = async () => {
      if (!account || userInfoSent) return;

      try {
        const claims = account.idTokenClaims as any;
        const cwid = claims?.["https://bayer.com/cwid"] || null;
        const division = claims?.["https://bayer.com/division"] || null;

        // Use localAccountId as UUID (it's in format: "uuid.uuid")
        const userId = account.localAccountId.split(".")[0];

        const userInfo = {
          user_id: userId,
          email: account.username,
          name: account.name || null,
          cwid: cwid,
          division: division,
        };

        const response = await fetch(`${API_BASE_URL}/api/addUserInfo`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userInfo),
        });

        if (response.ok) {
          setUserInfoSent(true);
        }
      } catch (error) {
        console.error("[User Info] Error:", error);
      }
    };

    sendUserInfo();
  }, [account, userInfoSent]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    if (account?.username) {
      try {
        await fetch(
          `${API_BASE_URL}/api/logoutUser?email=${encodeURIComponent(account.username)}`,
          { method: "POST" }
        );
      } catch (error) {
        console.error("[User Info] Logout error:", error);
      }
    }

    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  return (
    <header className="shadow-sm border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
      <div className="px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                {window.location.pathname === "/research/gene-disease-link" ? (
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-2xl shadow-2xl">
                    <span className="text-white font-bold text-2xl">🔗</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-14 h-14 rounded-2xl shadow-2xl overflow-hidden bg-transparent">
                    <img
                      src="/targetx-logo.png"
                      alt="TargetX Logo"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                )}

                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent leading-none">
                  {title}
                </h1>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400 transition-colors mt-1 tracking-wide">
                  {subtitle}
                </p>
              </div>
            </div>
          </div>

          {showSearch && (
            <div className="flex items-center space-x-6">
              {/* <div className="relative w-full max-w-2xl">
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="px-6 py-4 w-full text-lg border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-500/30 focus:border-blue-500 bg-white shadow-lg hover:shadow-xl transition-all duration-300 placeholder:text-gray-400"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {isSearching ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  ) : (
                    <svg
                      className="h-6 w-6 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  )}
                </div>
              </div> */}

              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen((prev) => !prev)}
                  className="flex items-center space-x-3 px-4 py-2.5 text-slate-700 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white rounded-xl transition-all duration-200 border border-transparent hover:border-blue-200"
                >
                  <div className="h-8 w-8 mr-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white font-semibold text-sm">
                    {account?.name?.charAt(0).toUpperCase() ?? "U"}
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium">{account?.name ?? "User"}</span>
                    {account?.username && (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {account.username}
                      </span>
                    )}
                  </div>
                  <svg
                    className={`w-2.5 h-2.5 ms-3 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="z-50 absolute right-0 mt-2 bg-white divide-y divide-gray-100 rounded-lg shadow-lg w-56 dark:bg-gray-700 border border-gray-100 dark:border-gray-600">
                    <div className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {account?.name ?? "User"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {account?.username ?? ""}
                      </p>
                    </div>
                    <ul className="py-1 text-sm text-gray-700 dark:text-gray-200">
                      <li>
                        <button
                          onClick={handleSignOut}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-red-600 dark:text-red-400 hover:text-red-700"
                        >
                          Sign out
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
